/**
 * 設定 → システム設定 の「日次【集計】の色」(2026-09-24 発注者指示「日次集計の色付けも設定画面で設定できるように」。P-61)。
 * 配色(現行 / 現行スプレッドシートと同じ)と、色を付けるしきい値を管理者が変える。保存先は `settings/staff_summary`。
 */
import { useEffect, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import Panel from '@core/components/ui/Panel';
import { DEFAULT_DISPLAY, fetchStaffSummaryDisplay, saveStaffSummaryDisplay, type StaffSummaryDisplay } from './staffSummaryDisplay';

type Key = keyof StaffSummaryDisplay;
const FIELDS: { key: Key; label: string; unit: string }[] = [
  { key: 'hour_red', label: '時間別取得件数を赤で塗る', unit: '件以上' },
  { key: 'hit_rate_good', label: '取得率を薄いオレンジにする', unit: '%以上' },
  { key: 'talk_per_appt_good_min', label: '1取得あたりの通話時間', unit: '分以下' },
  { key: 'work_per_appt_good_min', label: '1取得あたりの稼働時間', unit: '分以下' },
  { key: 'calls_per_hour_good', label: '1時間あたりの通話回数', unit: '回以上' },
  { key: 'apph_hi', label: '取得実績を青にする(給湯器)', unit: '件/h より多い' },
  { key: 'apph_lo', label: '取得実績を赤にする(給湯器)', unit: '件/h 未満' },
  { key: 'apph_hi_fuyohin', label: '取得実績を青にする(不用品)', unit: '件/h より多い' },
  { key: 'apph_lo_fuyohin', label: '取得実績を赤にする(不用品)', unit: '件/h 以下' },
];

export default function StaffSummaryColorsPanel({ onDirtyChange }: { onDirtyChange?: (dirty: boolean) => void }) {
  const toast = useToast();
  const { user } = useAuth();
  const [saved, setSaved] = useState<StaffSummaryDisplay>(DEFAULT_DISPLAY);
  const [value, setValue] = useState<StaffSummaryDisplay>(DEFAULT_DISPLAY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void fetchStaffSummaryDisplay()
      .then((d) => {
        if (!alive) return;
        setSaved(d);
        setValue(d);
      })
      .catch(() => undefined)
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const dirty = JSON.stringify(saved) !== JSON.stringify(value);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const setNum = (key: Key, text: string) => setValue((prev) => ({ ...prev, [key]: text.trim() === '' ? 0 : Number(text) }));

  async function save() {
    if (!user) return;
    for (const f of FIELDS) {
      const v = value[f.key];
      if (!Number.isFinite(v) || v < 0) {
        toast(`${f.label} は0以上の数字で入れてください`);
        return;
      }
    }
    setBusy(true);
    try {
      await saveStaffSummaryDisplay(value, user.id);
      setSaved(value);
      toast('日次【集計】の色を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      id="staff-summary-colors"
      title="日次【集計】の色"
      meta={dirty ? '未保存' : undefined}
      note="**全員に同じ設定が効きます**。配色は現行スプレッドシートと同じ(時間別取得件数の塗りつぶし・RANKの色など)で、**色を付けるしきい値**をここで変えられます(既定値はシートの条件付き書式と同じ)"
    >
      {loading ? (
        <div className="ui-empty">読み込み中…</div>
      ) : (
        <>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label>
                  {f.label}({f.unit})
                </label>
                <NumInput decimal value={String(value[f.key])} onChange={(t) => setNum(f.key, t)} />
              </div>
            ))}
          </div>
          <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
            <button type="button" className="ui-btn ui-btn-primary" disabled={busy || !dirty} onClick={() => void save()}>
              保存する
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" disabled={busy || !dirty} onClick={() => setValue(saved)}>
              元に戻す
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => setValue(DEFAULT_DISPLAY)}>
              しきい値を既定(シートと同じ)に
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}
